import { createBrowserRouter, Outlet, RouterProvider, ScrollRestoration, type RouteObject } from 'react-router';
import type { ComponentType } from 'react';
import type { Permission } from '@portal/contracts';
import { FullPageSpinner, RedirectIfAuthed, RequireAuth, RequireBusiness, RequirePermission } from '@/lib/guards';
import { SessionProvider } from '@/lib/session';
import { AppLayout } from '@/components/layout/app-layout';
import { NotFoundPage } from '@/pages/status-pages';
import { ErrorState } from '@/components/common/page';

function Root() {
  return (
    <SessionProvider>
      <Outlet />
      <ScrollRestoration />
    </SessionProvider>
  );
}

function RouteError({ error }: { error?: unknown }) {
  return <div className="mx-auto max-w-lg p-8"><ErrorState error={error ?? new Error('This page failed to load.')} onRetry={() => window.location.reload()} title="Something went wrong loading this page" /></div>;
}

type Loader = () => Promise<{ default: ComponentType }>;
/** Code-split a page; optionally require a permission (UX only — the API enforces it independently). */
const page = (load: Loader, permission?: Permission, what?: string): RouteObject => ({
  lazy: async () => {
    const C = (await load()).default;
    return { Component: () => (permission ? <RequirePermission permission={permission} what={what}><C /></RequirePermission> : <C />) };
  },
});

const routes: RouteObject[] = [
  {
    element: <Root />,
    errorElement: <RouteError />,
    hydrateFallbackElement: <FullPageSpinner />,
    children: [
      // The public landing page. Signed-in users are sent on to /dashboard by the page itself.
      { index: true, lazy: async () => ({ Component: (await import('@/features/landing/landing-page')).default }) },
      {
        element: <RedirectIfAuthed />,
        children: [
          { path: 'login', ...page(() => import('@/features/auth/login')) },
          { path: 'register', ...page(() => import('@/features/auth/register')) },
        ],
      },
      { path: 'forgot-password', lazy: async () => ({ Component: (await import('@/features/auth/recovery')).ForgotPasswordPage }) },
      { path: 'reset-password', lazy: async () => ({ Component: (await import('@/features/auth/recovery')).ResetPasswordPage }) },
      { path: 'verify-email', lazy: async () => ({ Component: (await import('@/features/auth/recovery')).VerifyEmailPage }) },
      { path: 'accept-invite', ...page(() => import('@/features/auth/accept-invite')) },
      { path: 'mfa', ...page(() => import('@/features/auth/mfa-challenge')) },
      {
        element: <RequireAuth />,
        children: [
          { path: 'onboarding', ...page(() => import('@/features/onboarding/onboarding')) },
          {
            element: <RequireBusiness />,
            children: [
              {
                element: <AppLayout />,
                children: [
                  { path: 'dashboard', ...page(() => import('@/features/dashboard/dashboard')) },
                  { path: 'invoices', ...page(() => import('@/features/invoices/list'), 'invoice:read', 'Invoices') },
                  { path: 'invoices/new', ...page(() => import('@/features/invoices/editor/invoice-editor'), 'invoice:write', 'Creating invoices') },
                  { path: 'invoices/:id/edit', ...page(() => import('@/features/invoices/editor/invoice-editor'), 'invoice:write', 'Editing invoices') },
                  { path: 'invoices/:id', ...page(() => import('@/features/invoices/detail/invoice-detail'), 'invoice:read', 'Invoices') },
                  { path: 'customers', ...page(() => import('@/features/customers/customers-page'), 'customer:read', 'Customers') },
                  { path: 'customers/:id', ...page(() => import('@/features/customers/customer-detail'), 'customer:read', 'Customers') },
                  { path: 'products', ...page(() => import('@/features/products/products-page'), 'catalog:read', 'The catalog') },
                  { path: 'imports', ...page(() => import('@/features/imports/imports-page'), 'import:read', 'The import centre') },
                  { path: 'imports/:id', ...page(() => import('@/features/imports/import-detail'), 'import:read', 'The import centre') },
                  { path: 'reports', ...page(() => import('@/features/reports/sales-report'), 'report:view', 'Reports') },
                  { path: 'reports/outstanding', ...page(() => import('@/features/reports/outstanding-report'), 'report:view', 'Reports') },
                  { path: 'reports/payments', ...page(() => import('@/features/reports/payments-report'), 'report:view', 'Reports') },
                  { path: 'reports/exports', ...page(() => import('@/features/reports/exports-page'), 'export:run', 'Exports') },
                  { path: 'settings/business', ...page(() => import('@/features/settings/business-settings'), 'business:read', 'Business settings') },
                  { path: 'settings/branches', ...page(() => import('@/features/settings/branches'), 'business:read', 'Branches') },
                  { path: 'settings/team', ...page(() => import('@/features/settings/team'), 'team:read', 'Team settings') },
                  { path: 'settings/integration', ...page(() => import('@/features/settings/integration'), 'integration:read', 'NRS integration') },
                  { path: 'settings/security', ...page(() => import('@/features/settings/security')) },
                  { path: 'settings/audit', ...page(() => import('@/features/settings/audit'), 'audit:view', 'The activity log') },
                  { path: '*', element: <NotFoundPage /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage standalone /> },
    ],
  },
];

const router = createBrowserRouter(routes);

export function App() {
  return <RouterProvider router={router} />;
}
